import React, { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Box, Dialog } from "@material-ui/core";

import {
  IPerson,
  MODAL_ALERT,
  MODAL_ALERT_CHOICE,
  ModalAlertChoiceProperties,
  ModalAlertProperties,
  ModalButtonType,
  CorpModalScreens,
  PolicyTier,
  ModalCategoryType,
  ModalNames,
  CorpSessionInfo,
  RewardsAccount,
} from "redmond";
import isEqual from "lodash-es/isEqual";
import {
  EASE_ONBOARDING_PRIMARY_ACTIVATION_CHANNEL,
  ONBOARDING_REFERRER_SOURCE,
  useUserContext,
  VXB,
} from "@capone/common";
import {
  CorporateButton,
  CorporateTypography,
  GenericInfoPopup,
  Icon,
  IconName,
  useDeviceTypes,
} from "halifax";
import { useExperimentIsVariant } from "@capone/experiments";
import AdditionalDetails from "./components/AdditionalDetails";
import CreateTraveler from "./components/CreateTraveler";
import { trackEvent } from "../../../api/v1/trackEvent";
import { PATH_BUSINESS } from "../../../utils/urlPaths";
import { getCorpPassengerList } from "./utils/getCorpPassengerList";
import { updateCorpPassengerList } from "./utils/updateCorpPassengerList";
import { resolvePrimaryWelcomeVariant } from "./utils/resolvePrimaryWelcomeVariant";
import classNames from "./styles.module.css";
import { updateUserSeenModal } from "../../../api/v1/user/updateUserSeenModal";
import { Welcome } from "./components/Welcome";
import { YouAreAllSet } from "./components/YouAreAllSet";

export interface IOnboardingModalProps {
  policies: PolicyTier;
  currentAccount: RewardsAccount | undefined;
}

const getMobileDialogMaxWidth = (width: number) => {
  if (width <= 320) return "320px";
  if (width <= 360) return "360px";
  if (width <= 390) return "390px";
  return "428px";
};

export const OnboardingModal = ({
  policies,
  currentAccount,
}: IOnboardingModalProps): React.ReactElement => {
  const {
    sessionInfo,
    modalManagerProps: { closeActiveModal, activeModal },
  } = useUserContext("capone-corporate");
  const { matchesMobile } = useDeviceTypes();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [travelers, setTravelers] = useState<IPerson[]>([]);
  const [isLoadingTravelers, setIsLoadingTravelers] = useState(true);
  const [isLoadingSetupBusiness, setIsLoadingSetupBusiness] = useState(false);
  const [isCloseWarningOpen, setIsCloseWarningOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const [selectedTraveler, setSelectedTraveler] = useState<
    IPerson | undefined
  >();

  const currentScreenNameForEvent = useMemo(() => {
    const screens: Record<number, CorpModalScreens> = {
      0: CorpModalScreens.ONBOARDING_WELCOME,
      1: CorpModalScreens.ONBOARDING_DETAILS,
      2: CorpModalScreens.ONBOARDING_ADDITIONAL,
      3: CorpModalScreens.YOU_ARE_ALL_SET,
    };
    return screens[step];
  }, [step]);

  const PROFILE_CREATION_STEP = 2;

  const isPriorBasicProfileCreation = useMemo(
    () => step <= PROFILE_CREATION_STEP,
    [step]
  );

  const closeWarningTitle = isPriorBasicProfileCreation
    ? "Your traveler profile won’t be saved"
    : "Your additional details won’t be saved";

  const closeWarningPrimaryButtonText = isPriorBasicProfileCreation
    ? "Complete traveler profile"
    : "Complete details";

  const isVXB = currentAccount?.productDisplayName === VXB;

  const corporateInfo = (sessionInfo as CorpSessionInfo)?.corporateInfo;
  const isPrimary = corporateInfo?.cap1Role === "PR";
  // Case-insensitive: the activation channel round-trips through URP server-side
  // sanitization and has been observed lowercase at runtime, while the constant
  // is cased `_PR`. Normalize both sides so the EASE exclusion fires regardless.
  const isEaseOnboardingUser =
    corporateInfo?.activationChannel?.toLowerCase() ===
    EASE_ONBOARDING_PRIMARY_ACTIVATION_CHANNEL.toLowerCase();
  const isRevampVariantAvailable = useExperimentIsVariant(
    "corp-primary-welcome-modal-revamp",
    "available"
  );
  // The primary welcome modal has two variations: "available" (Variant B, the
  // Phase 0 revamp experiment) and "control". EASE-onboarded users are
  // excluded from the test and always see control. Non-primary users have no
  // variation (the prop is ignored in the non-primary branch of Welcome).
  const primaryWelcomeVariant = resolvePrimaryWelcomeVariant({
    isPrimary,
    isEaseOnboardingUser,
    isRevampVariantAvailable,
  });

  useEffect(() => {
    const open = activeModal === "onboarding";
    setIsOpen(open);
  }, [activeModal]);

  useEffect(() => {
    if (sessionInfo && sessionInfo.csrfToken) {
      getCorpPassengerList(sessionInfo)
        .then((corpTravelers) => {
          setTravelers(corpTravelers);
          const existingLoggedUserTraveler = corpTravelers.find(
            (traveler) => traveler.isDefaultProfile
          );
          setSelectedTraveler(existingLoggedUserTraveler);
          setIsLoadingTravelers(false);
        })
        .catch(() => setIsLoadingTravelers(false));
    }
  }, [sessionInfo]);

  const showCloseWarning = useCallback(() => {
    setIsCloseWarningOpen(true);
    trackEvent({
      eventName: MODAL_ALERT,
      properties: {
        type: "exit_onboarding",
        screen: currentScreenNameForEvent,
        primary_button: closeWarningPrimaryButtonText,
        secondary_button: "leave_without_saving",
        modal_subtitle: "",
        modal_title: closeWarningTitle,
        agent_title: closeWarningTitle,
        agent_subtitle: "",
        step: `${currentScreenNameForEvent}`,
        funnel: "other",
        category: ModalCategoryType.FEATURE,
      } satisfies ModalAlertProperties,
    });
  }, [currentScreenNameForEvent, trackEvent]);

  const hideCloseWarning = useCallback(
    (dispatchEvent = true, buttonChoice = ModalButtonType.PRIMARY) => {
      setIsCloseWarningOpen(false);
      if (dispatchEvent) {
        trackEvent({
          eventName: MODAL_ALERT_CHOICE,
          properties: {
            type: "exit_onboarding",
            screen: currentScreenNameForEvent,
            button_choice: buttonChoice,
            primary_button: closeWarningPrimaryButtonText,
            secondary_button: "leave_without_saving",
            modal_subtitle: "",
            modal_title: closeWarningTitle,
            agent_title: closeWarningTitle,
            agent_subtitle: "",
            step: `${currentScreenNameForEvent}`,
            funnel: "other",
            category: ModalCategoryType.FEATURE,
          } satisfies ModalAlertChoiceProperties,
        });
      }
    },
    [currentScreenNameForEvent, trackEvent]
  );

  const close = useCallback(() => {
    hideCloseWarning(true, ModalButtonType.CLOSE);
    updateUserSeenModal({
      modalName: isVXB
        ? ModalNames.HAS_SEEN_VXB_ONBOARDING_MODAL
        : ModalNames.HAS_SEEN_ONBOARDING_MODAL,
    });
    setIsOpen(false);
    closeActiveModal("onboarding");
  }, [hideCloseWarning, isVXB]);

  const handleSetupBusiness = () => {
    setIsLoadingSetupBusiness(true);
    updateUserSeenModal({
      modalName: isVXB
        ? ModalNames.HAS_SEEN_VXB_ONBOARDING_MODAL
        : ModalNames.HAS_SEEN_ONBOARDING_MODAL,
    })
      .catch(() => { })
      .finally(() => {
        window.location.href = `${PATH_BUSINESS}user-management?${ONBOARDING_REFERRER_SOURCE}`;
      });
  };

  const skip = useCallback(() => {
    close();
    trackEvent({
      eventName: MODAL_ALERT_CHOICE,
      properties: {
        type: "exit_onboarding",
        screen: currentScreenNameForEvent,
        button_choice: ModalButtonType.SECONDARY,
        primary_button: "complete_traveler_profile",
        secondary_button: "leave_without_saving",
        modal_subtitle: "",
        modal_title: closeWarningTitle,
        agent_title: closeWarningTitle,
        agent_subtitle: "",
        step: `${currentScreenNameForEvent}`,
        funnel: "other",
        category: ModalCategoryType.FEATURE,
      } satisfies ModalAlertChoiceProperties,
    });
  }, [close, currentScreenNameForEvent, trackEvent]);

  const nextStep = useCallback(() => {
    setStep(step + 1);
  }, [step]);

  const prevStep = useCallback(
    (_event?: React.MouseEvent | null, stepsToGoBack = 1) => {
      if (step > 0) {
        setStep(step - stepsToGoBack);
      }
    },
    [step]
  );

  const onSelectTraveler = useCallback(
    (traveler: IPerson) => {
      setSelectedTraveler(traveler);
    },
    [setSelectedTraveler]
  );

  const createNewTraveler = useCallback(() => {
    setSelectedTraveler(undefined);
    nextStep();
  }, [nextStep]);

  const removeUndefinedValuesFromTraveler = (traveler: IPerson) => {
    const travelerCopy = { ...traveler };
    Object.keys(travelerCopy).forEach(
      (key: string) =>
        travelerCopy[key as keyof IPerson] === undefined &&
        delete travelerCopy[key as keyof IPerson]
    );

    return travelerCopy;
  };

  const hasMadeChangesToTraveler = useCallback(
    (traveler: IPerson) => {
      const travelerCopy = removeUndefinedValuesFromTraveler(traveler);
      const selectedTravelerCopy = removeUndefinedValuesFromTraveler(
        selectedTraveler!
      );

      return !isEqual(travelerCopy, selectedTravelerCopy);
    },
    [selectedTraveler]
  );

  const handleUpdateTraveler = useCallback(
    (traveler: IPerson): Promise<void> =>
      new Promise<void>((resolve, reject) => {
        updateCorpPassengerList({
          person: traveler,
          currentTravelers: travelers,
          sessionInfo,
          source: "traveler_onboarding",
        })
          .then((updatedTraveler) => {
            setSelectedTraveler(updatedTraveler);
            nextStep();
            resolve();
          })
          .catch((error) => {
            reject(error);
          });
      }),
    [hasMadeChangesToTraveler, sessionInfo, travelers, nextStep]
  );

  const goBackFromCreateTraveler = useCallback(() => {
    if (isLoadingTravelers || travelers.length) {
      prevStep(null);
    } else {
      prevStep(null, 2);
    }
  }, [isLoadingTravelers, travelers, prevStep]);

  const steps = useMemo(() => {
    const stepsArray = [
      <Welcome
        key="welcome"
        primaryAction={nextStep}
        secondaryAction={handleSetupBusiness}
        isLoading={isLoadingTravelers}
        username={sessionInfo?.userInfo.firstName || ""}
        onClose={close}
        isPrimary={isPrimary}
        isSecondaryActionLoading={isLoadingSetupBusiness}
        isMobile={matchesMobile}
        currentAccount={currentAccount}
        primaryVariant={primaryWelcomeVariant}
      />,
    ];
    stepsArray.push(
      <CreateTraveler
        key="create-profile"
        traveler={selectedTraveler}
        primaryAction={handleUpdateTraveler}
        secondaryAction={goBackFromCreateTraveler}
        onClose={showCloseWarning}
        isMobile={matchesMobile}
      />,
      <AdditionalDetails
        key="additional-details"
        traveler={selectedTraveler!}
        primaryAction={handleUpdateTraveler}
        secondaryAction={prevStep}
        onClose={skip}
        isMobile={matchesMobile}
      />
    );

    stepsArray.push(
      <YouAreAllSet
        key="you-are-all-set"
        primaryAction={close}
        secondaryAction={prevStep}
        onClose={close}
      />
    );

    return stepsArray;
  }, [
    nextStep,
    showCloseWarning,
    createNewTraveler,
    travelers,
    isLoadingTravelers,
    selectedTraveler,
    onSelectTraveler,
    handleUpdateTraveler,
    goBackFromCreateTraveler,
    prevStep,
    policies,
    close,
    isLoadingSetupBusiness,
    currentAccount,
    isPrimary,
    primaryWelcomeVariant,
    sessionInfo,
  ]);

  const dialogMaxWidth = useMemo(() => {
    if (matchesMobile) return undefined;
    return "lg";
  }, [matchesMobile]);

  return (
    <>
      <Dialog
        open={isOpen}
        aria-labelledby={
          step === 3
            ? "you-are-all-set-title you-are-all-set-subtitle"
            : "onboarding-title onboarding-subtitle"
        }
        aria-describedby="onboarding-description"
        data-testid="corporate-onboarding-modal"
        onClose={showCloseWarning}
        maxWidth={dialogMaxWidth}
        fullWidth={matchesMobile}
        className={classNames.modal}
        scroll="paper"
        key={`onboarding-step-${step}`}
        PaperProps={{
          style: matchesMobile
            ? {
              width: "90%",
              maxWidth: getMobileDialogMaxWidth(windowWidth),
              margin: "auto",
              display: "flex",
              flexDirection: "column",
              maxHeight: "85vh",
            }
            : step === 0
              ? { borderRadius: 8, overflow: "hidden" }
              : undefined,
        }}
      >
        <Box className={clsx(classNames.stepContainer, `step-${step}`)}>
          {steps[step]}
        </Box>
      </Dialog>
      {/* note: we need to conditionally reender this because of a race condition of when this modal closes and when the onboarding modal closes that doesn't reset the page overflow correctly */}
      {isOpen && (
        <GenericInfoPopup
          className={classNames.closeWarning}
          isMobile={matchesMobile}
          open={isCloseWarningOpen}
          showCloseButton
          disableEnforceFocus
          onClose={hideCloseWarning}
          subtitle={
            isPriorBasicProfileCreation ? (
              <>
                <CorporateTypography variant="subtitle2" fontWeight="light">
                  Complete your traveler profile now to automatically apply your
                  information and loyalty programs during booking.
                </CorporateTypography>
                <br />
                <CorporateTypography variant="subtitle2" fontWeight="light">
                  Not quite ready? You can always add profiles later under the
                  Traveler Profile section or during checkout.
                </CorporateTypography>
              </>
            ) : (
              <CorporateTypography variant="subtitle2" fontWeight="light">
                Not quite ready? You can always add information to your profile
                later under the Traveler Profile section or during checkout.
              </CorporateTypography>
            )
          }
          title={
            <CorporateTypography
              variant={matchesMobile ? "h2" : "h4"}
              fontWeight="light"
            >
              {closeWarningTitle}
            </CorporateTypography>
          }
          image={<Icon className="error-icon" name={IconName.Caution} />}
          buttons={
            <Box
              style={{
                display: "flex",
                flexDirection: matchesMobile ? "column" : "row",
                width: matchesMobile ? "100%" : "auto",
                gap: matchesMobile ? "16px" : "20px",
              }}
            >
              {matchesMobile && (
                <CorporateButton
                  size="large"
                  onClick={() => hideCloseWarning()}
                  fullWidth
                >
                  {closeWarningPrimaryButtonText}
                </CorporateButton>
              )}
              <CorporateButton
                size={matchesMobile ? "large" : "medium"}
                variant="outlined"
                onClick={skip}
                fullWidth={matchesMobile}
              >
                Leave without saving
              </CorporateButton>
              {!matchesMobile && (
                <CorporateButton
                  size="medium"
                  onClick={() => hideCloseWarning()}
                >
                  {closeWarningPrimaryButtonText}
                </CorporateButton>
              )}
            </Box>
          }
        />
      )}
    </>
  );
};
